import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  doSettingsSeveritiesCreate,
  doSettingsSeveritiesUpdate,
  eventColorSelected,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { PageHeader } from 'components/PageHeader';
import { selectRemoteSettingsSeverity } from 'store/remote/selectors';
import { useSelector } from 'hooks/useSelector';
import { Severity } from 'types/Severities';
import { TextField } from 'components/Field/TextField';
import { CardRow } from 'components/CardRow';
import { Icon, ICONS } from 'components/Icon';
import { theme } from 'lib/theme';
import { View } from 'react-native';
import { randomKey } from 'lib/riskbase';
import { doAddEvent, doRemoveEvent } from 'lib/events';

export const SettingsSeverityModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsSeverityModal>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();
  const pageKey = randomKey();

  const { severityId, organisationId } = route.params;

  const severity = useSelector((state) =>
    severityId ? selectRemoteSettingsSeverity(state, severityId) : undefined,
  );

  const initDataRef = React.useRef<Severity>(
    cloneDeep(
      severity
        ? severity
        : {
            id: '',
            name: '',
            description: '',
            color: '#F5CD10',
            position: 1,
          },
    ),
  );
  const dataRef = React.useRef(cloneDeep(initDataRef.current));
  const isDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleOpenColorPickerModal = React.useCallback(
    (color: string) => {
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.ColorPicker,
          params: {
            pageKey,
            color,
          },
        }),
      );
    },
    [dispatch, pageKey],
  );

  const handleSave = React.useCallback(() => {
    if (dataRef.current.name.length === 0) {
      dispatch(
        ALERT({
          text: 'Add a name to save the severity',
        }),
      );
      return;
    }

    dispatch(
      severityId
        ? doSettingsSeveritiesUpdate({
            organisationId: organisationId,
            severity: dataRef.current,
          })
        : doSettingsSeveritiesCreate({
            organisationId: organisationId,
            severity: {
              name: dataRef.current.name,
              description: dataRef.current.description,
              color: dataRef.current.color,
            },
          }),
    );
  }, [dispatch, organisationId, severityId]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = React.useCallback(
    <K extends keyof typeof dataRef.current>(
      key: K,
      value: (typeof dataRef.current)[K],
      doForceUpdate: boolean = false,
    ) => {
      dataRef.current[key] = value;

      if (doForceUpdate || isDirtyRef.current !== isDirty()) {
        forceUpdate();
      }
    },
    [forceUpdate, isDirty],
  );

  React.useEffect(() => {
    const event = doAddEvent(eventColorSelected, (action) => {
      if (action.payload.key === pageKey) {
        handleChangeValue('color', action.payload.color, true);
      }
    });

    return () => doRemoveEvent(event);
  }, [pageKey, handleChangeValue]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Task Severity"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <View
        style={{
          flexDirection: 'row',
          marginHorizontal: theme.padding,
          gap: theme.padding,
        }}
      >
        <Card
          style={{ flex: 1 }}
          marginHorizontal={false}
          label="Name"
          required
        >
          <TextField
            onChange={(value) => handleChangeValue('name', value)}
            value={dataRef.current.name}
            onEsc={handleClose}
            autoFocusWeb={true}
            autoFocusDelay
            maxLength={60}
          />
        </Card>
        <Card
          marginHorizontal={false}
          labelStyle={{ display: 'contents' }}
          label="Colour"
        >
          <CardRow
            navigationIcon={ICONS.CaretDownSolid}
            navigationIconSize={12}
            onPress={() => handleOpenColorPickerModal(dataRef.current.color)}
          >
            <Icon source={ICONS.CircleSolid} color={dataRef.current.color} />
          </CardRow>
        </Card>
      </View>
      <View
        style={{
          flexDirection: 'row',
          marginHorizontal: theme.padding,
        }}
      >
        <Card style={{ flex: 1 }} marginHorizontal={false} label="Description">
          <TextAreaField
            onChange={(value) => handleChangeValue('description', value)}
            value={dataRef.current.description}
            onEsc={handleClose}
            autoFocusWeb={false}
            autoFocusDelay
            maxLength={512}
          />
        </Card>
      </View>
    </Page>
  );
};
